import { useCallback, useContext, useEffect, useState } from "react";
import type { UserWithEmail } from "lens-platform-sdk";
import { useLensPlatformClient } from "./useLensPlatformClient";
import { TokenContext } from "../providers/token-provider";

export const useFetchProfile = () => {
  const [profile, setProfile] = useState<UserWithEmail | null>(null);
  const [profileError, setProfileError] = useState<unknown | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const lensPlatformClient = useLensPlatformClient();
  const { token } = useContext(TokenContext);

  const fetchProfile = useCallback(async (username: string) => {
    if (!lensPlatformClient) {
      return;
    }

    try {
      const userProfile = await lensPlatformClient.user.getOne(
        {
          username,
        },
        "join=userAttributes",
      );

      setProfile(userProfile);
    } catch (error) {
      setProfileError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;

    // eslint-disable-next-line security/detect-possible-timing-attacks
    if (token === undefined) {
      // Still loading
      return () => {};
    }

    if (!token?.preferred_username) {
      setIsLoading(false);

      return () => {};
    }

    setIsLoading(true);
    fetchProfile(token?.preferred_username).finally(() => {
      if (cancelled === false) {
        setIsLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return {
    profile,
    isProfileLoading: isLoading,
    profileError,
    updateProfile: fetchProfile,
  };
};
